import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Loader2, Building2, MapPin, ChevronRight } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showOfficeSelection, setShowOfficeSelection] = useState(false);
  const [availableOffices, setAvailableOffices] = useState([]);
  const { login, completeLoginWithOffice, cancelOfficeSelection, pendingLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/dashboard';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(email, password);
      
      if (result.requiresOfficeSelection) {
        // Show office selection screen
        setAvailableOffices(result.availableOffices);
        setShowOfficeSelection(true);
        setLoading(false);
        return;
      }
      
      toast.success('Accesso effettuato con successo');
      navigate(from, { replace: true });
    } catch (error) {
      console.error('Login error:', error);
      const status = error.response?.status;
      const message = error.response?.data?.detail;
      
      if (status === 429) {
        // Rate limit exceeded
        toast.error(message || 'Troppi tentativi. Riprova più tardi.', {
          duration: 5000,
          icon: '⏱️'
        });
      } else {
        toast.error(message || 'Credenziali non valide');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOfficeSelect = async (officeId) => {
    setLoading(true);
    try {
      await completeLoginWithOffice(officeId);
      toast.success('Accesso effettuato con successo');
      navigate(from, { replace: true });
    } catch (error) {
      console.error('Office selection error:', error);
      toast.error(error.response?.data?.detail || 'Errore nella selezione ufficio');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowOfficeSelection(false);
    setAvailableOffices([]);
    cancelOfficeSelection();
  };

  // Office Selection Screen
  if (showOfficeSelection) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F5FBF9] to-[#E8F5F2] p-4" role="main">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-100">
            {/* Logo */}
            <header className="text-center mb-6">
              <img
                src="https://customer-assets.emergentagent.com/job_info-security-fdl/artifacts/0n58s6iv_LOGO_e-CAA_light.png"
                alt="Logo e-CAA"
                className="h-12 w-auto mx-auto mb-4"
              />
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Seleziona Ufficio CAA</h1>
              <p className="text-slate-500 text-sm">Il tuo account è associato a più uffici. Seleziona l'ufficio per questa sessione.</p>
            </header>

            {/* Office List */}
            <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="office-selection-list">
              {availableOffices.map((office) => (
                <button
                  key={office.office_id}
                  onClick={() => handleOfficeSelect(office.office_id)}
                  disabled={loading}
                  className="w-full flex items-center gap-3 p-4 rounded-xl border border-slate-200 hover:border-[#3BA99C] hover:bg-[#F5FBF9] transition-all text-left group disabled:opacity-50"
                  data-testid={`office-option-${office.office_id}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-[#3BA99C]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#3BA99C]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 truncate">{office.name}</p>
                    <p className="text-sm text-slate-500 font-mono">{office.code}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-[#3BA99C] transition-colors flex-shrink-0" />
                </button>
              ))}
            </div>

            {loading && (
              <div className="flex items-center justify-center gap-2 mt-4 text-[#3BA99C]">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Accesso in corso...</span>
              </div>
            )}

            {/* Back Button */}
            <button
              onClick={handleBackToLogin}
              disabled={loading}
              className="w-full mt-4 text-sm text-slate-500 hover:text-slate-700 font-medium disabled:opacity-50"
              data-testid="back-to-login-btn"
            >
              ← Torna al login
            </button>
          </div>
        </div>
      </main>
    );
  }

  // Normal Login Screen
  return (
    <main className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F5FBF9] to-[#E8F5F2] p-4" role="main">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-100">
          {/* Logo */}
          <header className="text-center mb-8">
            <img
              src="https://customer-assets.emergentagent.com/job_info-security-fdl/artifacts/0n58s6iv_LOGO_e-CAA_light.png"
              alt="Logo e-CAA - Centro di Assistenza Agricola"
              className="h-16 w-auto mx-auto mb-4"
            />
            <h1 className="text-3xl font-bold text-slate-900 mb-2">e-CAA-CONFAGRICOLTURA</h1>
            <p className="text-slate-500">Valutazione Maturità Sicurezza Informazioni</p>
          </header>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form" aria-label="Form di accesso">
            <div>
              <label htmlFor="email-input" className="block text-sm font-medium text-slate-700 mb-2">
                Email
              </label>
              <input
                id="email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                aria-required="true"
                autoComplete="email"
                className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3BA99C] focus:border-transparent transition-all"
                placeholder="email@esempio.it"
                data-testid="email-input"
              />
            </div>

            <div>
              <label htmlFor="password-input" className="block text-sm font-medium text-slate-700 mb-2">
                Password
              </label>
              <input
                id="password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                aria-required="true"
                autoComplete="current-password"
                className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3BA99C] focus:border-transparent transition-all"
                placeholder="••••••••"
                data-testid="password-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              aria-busy={loading}
              aria-label={loading ? 'Accesso in corso' : 'Accedi al sistema'}
              className="w-full bg-[#3BA99C] hover:bg-[#2D8A7F] text-white font-semibold py-3 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3BA99C]"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                  <span>Accesso in corso...</span>
                </>
              ) : (
                'Accedi'
              )}
            </button>
          </form>

          {/* Footer Links */}
          <nav className="mt-6 text-center" aria-label="Link aggiuntivi">
            <button
              onClick={() => navigate('/reset-password')}
              className="text-sm text-[#3BA99C] hover:text-[#2D8A7F] font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3BA99C] rounded"
              data-testid="forgot-password-link"
            >
              Password dimenticata?
            </button>
          </nav>
        </div>

        {/* Partner Logos */}
        <footer className="flex items-center justify-center gap-6 mt-8" role="contentinfo">
          <a href="https://www.confagricoltura.it" target="_blank" rel="noopener noreferrer" aria-label="Vai al sito Confagricoltura (si apre in una nuova finestra)">
            <img
              src="/logo-confagricoltura-small.png"
              alt="Logo Confagricoltura"
              className="h-10 w-auto opacity-70 hover:opacity-100 transition-opacity"
            />
          </a>
          <a href="https://www.etom.it" target="_blank" rel="noopener noreferrer" aria-label="Vai al sito Etom (si apre in una nuova finestra)">
            <img
              src="https://customer-assets.emergentagent.com/job_info-security-fdl/artifacts/xabqsord_LOGO_BIG_GRADIENT_PRINT_No%20sfondo_No%20Payload_Light.png"
              alt="Logo Etom"
              className="h-10 w-auto opacity-70 hover:opacity-100 transition-opacity"
            />
          </a>
        </footer>
      </div>
    </main>
  );
};

export default Login;